import React, { useState, useEffect } from 'react';
import { getAllAppointments, updateAppointmentStatus } from '../../services/appointmentService';
import { formatTime, formatDate, formatDateFull } from '../../utils/appointmentUtils';

const AppointmentManagement = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'confirmed'
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [appointmentToConfirm, setAppointmentToConfirm] = useState(null);

  // Fetch appointments on component mount
  useEffect(() => {
    fetchAppointments();
  }, []);

  // Fetch all appointments
  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const appointmentsData = await getAllAppointments();
      setAppointments(appointmentsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError('Failed to load appointments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Open confirmation modal
  const openConfirmModal = (appointment) => {
    setAppointmentToConfirm(appointment);
    setConfirmModalOpen(true);
  };

  // Close confirmation modal
  const closeConfirmModal = () => {
    setAppointmentToConfirm(null);
    setConfirmModalOpen(false);
  };

  // Handle confirming an appointment
  const handleConfirmAppointment = async () => {
    if (!appointmentToConfirm) return;
    
    try {
      setLoading(true);
      
      await updateAppointmentStatus(appointmentToConfirm.id, 'confirmed');
      
      // Update the local state to reflect the change in both appointment status and payment status
      setAppointments(prevAppointments => 
        prevAppointments.map(appointment => 
          appointment.id === appointmentToConfirm.id 
            ? { 
                ...appointment, 
                status: 'confirmed', 
                paymentStatus: 'processing',
                updatedAt: new Date() 
              } 
            : appointment
        )
      );
      
      setSuccess(`Appointment confirmed successfully. Payment will be captured automatically.`);
      setTimeout(() => setSuccess(null), 3000);
      closeConfirmModal();
    } catch (err) {
      console.error('Error confirming appointment:', err);
      setError('Failed to confirm appointment. Please try again.');
      closeConfirmModal();
    } finally {
      setLoading(false);
    }
  };

  // Filter appointments based on status
  const filteredAppointments = appointments.filter(appointment => {
    if (filter === 'all') return true;
    return appointment.status === filter;
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Appointment Management</h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded ${
              filter === 'all'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded ${
              filter === 'pending'
                ? 'bg-yellow-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('confirmed')}
            className={`px-4 py-2 rounded ${
              filter === 'confirmed'
                ? 'bg-green-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Confirmed
          </button>
          <button
            onClick={fetchAppointments}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded"
          >
            Refresh
          </button>
        </div>
      </div>

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Appointments List */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredAppointments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No {filter !== 'all' ? filter : ''} appointments found.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Practice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Appointment Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAppointments.map((appointment) => (
                <tr key={appointment.id}>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {appointment.userInfo?.name || 'Unknown Patient'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {appointment.userInfo?.email || 'No email provided'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {appointment.userInfo?.phone || 'No phone provided'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {appointment.practiceName || 'Unknown Practice'}
                    </div>
                    <div className="text-sm text-gray-500">
                      ID: {appointment.practiceId || 'Unknown'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {appointment.date ? formatDateFull(appointment.date) : 'Unknown Date'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {appointment.slot?.startTime && appointment.slot?.endTime ? 
                        `${formatTime(appointment.slot.startTime)} - ${formatTime(appointment.slot.endTime)}` : 
                        'Unknown Time'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {appointment.userInfo?.issue || 'General Appointment'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      appointment.status === 'confirmed' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {appointment.status || 'pending'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      appointment.paymentStatus === 'completed' 
                        ? 'bg-green-100 text-green-800' 
                        : appointment.paymentStatus === 'processing'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {appointment.paymentStatus || 'pending'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">
                      {appointment.createdAt ? formatDate(appointment.createdAt.toDate().toISOString().split('T')[0]) : 'Unknown'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {appointment.status === 'confirmed' ? (
                      <div>
                        <span className="text-gray-500">Confirmed</span>
                        {appointment.paymentStatus === 'completed' && (
                          <div className="text-xs text-green-600 mt-1">Payment Processed</div>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => openConfirmModal(appointment)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Confirm
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModalOpen && appointmentToConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-medium mb-4 text-red-600">Confirm Appointment</h3>
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                <strong>Warning:</strong> Confirming this appointment is <span className="text-red-600 font-bold">irreversible</span> 
                {appointmentToConfirm.paymentStatus !== 'completed' && " and will process payment from the patient's account."}
              </p>
              <p className="text-gray-700 mb-4">
                Are you sure you want to confirm this appointment for:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="font-medium">{appointmentToConfirm.userInfo?.name || 'Unknown Patient'}</p>
                <p>{appointmentToConfirm.date ? formatDateFull(appointmentToConfirm.date) : 'Unknown Date'}</p>
                <p>
                  {appointmentToConfirm.slot?.startTime && appointmentToConfirm.slot?.endTime ? 
                    `${formatTime(appointmentToConfirm.slot.startTime)} - ${formatTime(appointmentToConfirm.slot.endTime)}` : 
                    'Unknown Time'}
                </p>
                <p className="mt-2">Practice: {appointmentToConfirm.practiceName || 'Unknown Practice'}</p>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={closeConfirmModal}
                className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAppointment}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                {appointmentToConfirm.paymentStatus === 'completed' 
                  ? 'Confirm Appointment' 
                  : 'Confirm & Process Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentManagement;
